import { get, post } from '../lib/request'
import { wait } from '../lib/wait'

const MODEL_HOST = process.env.REACT_APP_MODEL_API_URL

class ModelAPI {
  token: string

  constructor() {
    this.token = ''
  }

  headers = () => ({
    'Authorization': `Bearer ${this.token}`
  })

  setToken(token: string) {
    this.token = token
  }

  getModelConfiguration = async (eventId: string) => {
    const response = await get<ModelAPI.ConfigurationResponse>({
      url: `${MODEL_HOST}/api/events/${eventId}/configuration`,
      headers: this.headers()
    })
    return response.data
  }

  resyncConfiguration = async (eventId: string) => {
    const data: ModelAPI.ConfigureRequest = {
      type: 'CONFIGURE',
      eventId
    }
    const response = await post<{}>({
      url: `${MODEL_HOST}/api/messages`,
      headers: this.headers(),
      body: data
    })
    return response.data
  }

  getModelTeamResults = async (eventId: string, roundId: number, teamId: number) => {
    const response = await get<ModelAPI.TeamResultsResponse>({
      url: `${MODEL_HOST}/api/events/${eventId}/rounds/${roundId}/teams/${teamId}/results`,
      headers: this.headers()
    })
    return response.data
  }

  recalculateAndSculptAllResults = async (eventId: string, roundId: number) => {
    for (let round = 1; round <= roundId; round++) {
      const data: ModelAPI.CalculateResultsRequest = {
        type: 'CALCULATE_RESULTS',
        eventId,
        roundId: round
      }

      await post<{}>({
        url: `${MODEL_HOST}/api/messages`,
        headers: this.headers(),
        body: data
      })

      const data2: ModelAPI.SculptTreasuryRequest = {
        type: 'SCULPT_TREASURY',
        eventId,
        roundId: round
      }
      await post<{}>({
        url: `${MODEL_HOST}/api/messages`,
        headers: this.headers(),
        body: data2
      })
      let iterations = 0
      while (true) {
        const configuration = await this.getModelConfiguration(eventId)
        if (configuration.state === 'round-sculpted') {
          break
        }
        if (iterations > 100) {
          throw new Error('Timedout sculpting all rounds')
        }
        iterations++
        await wait(2000)
      }


      const data3: ModelAPI.CalculateReportingRequest = {
        type: 'CALCULATE_REPORTING',
        eventId,
        roundId: round
      }
      await post<{}>({
        url: `${MODEL_HOST}/api/messages`,
        headers: this.headers(),
        body: data3
      })
    }

    return null
  }

  recalculateAllResults = async (eventId: string, roundId: number) => {
    for (let round = 1; round <= roundId; round++) {
      const data: ModelAPI.CalculateResultsRequest = {
        type: 'CALCULATE_RESULTS',
        eventId,
        roundId: round
      }

      await post<{}>({
        url: `${MODEL_HOST}/api/messages`,
        headers: this.headers(),
        body: data
      })
    }

    return null
  }

  recalculateResults = async (eventId: string, roundId: number) => {
    const data: ModelAPI.CalculateResultsRequest = {
      type: 'CALCULATE_RESULTS',
      eventId,
      roundId
    }
    const response = await post<{}>({
      url: `${MODEL_HOST}/api/messages`,
      headers: this.headers(),
      body: data
    })
    return response.data
  }

  uploadResults = async (eventId: string, roundId: number) => {
    const data: ModelAPI.UploadResultsRequest = {
      type: 'UPLOAD_RESULTS',
      eventId,
      roundId
    }
    const response = await post<{}>({
      url: `${MODEL_HOST}/api/messages`,
      headers: this.headers(),
      body: data
    })
    return response.data
  }

  recalculateReporting = async (eventId: string, roundId: number) => {
    const data: ModelAPI.CalculateReportingRequest = {
      type: 'CALCULATE_REPORTING',
      eventId,
      roundId
    }
    const response = await post<{}>({
      url: `${MODEL_HOST}/api/messages`,
      headers: this.headers(),
      body: data
    })
    return response.data
  }

  sculptResults = async (eventId: string, roundId: number) => {
    const data: ModelAPI.SculptTreasuryRequest = {
      type: 'SCULPT_TREASURY',
      eventId,
      roundId
    }
    const response = await post<{}>({
      url: `${MODEL_HOST}/api/messages`,
      headers: this.headers(),
      body: data
    })
    return response.data
  }

  getDecisions = async (eventId: string, roundId: number) => {
    const response = await get<ModelAPI.DecisionsResponse>({
      url: `${MODEL_HOST}/api/events/${eventId}/rounds/${roundId}/decisions`,
      headers: this.headers()
    })
    return response.data
  }

  getMarketShare = async (eventId: string, roundId: number) => {
    const response = await get<ModelAPI.MarketShareResponse>({
      url: `${MODEL_HOST}/api/events/${eventId}/rounds/${roundId}/marketshare`,
      headers: this.headers()
    })
    return response.data
  }

  getReportingResults = async (eventId: string, roundId: number) => {
    const response = await get<ModelAPI.Reporting.ReportingResults>({
      url: `${MODEL_HOST}/api/events/${eventId}/rounds/${roundId}/reporting`,
      headers: this.headers()
    })
    return response.data
  }

  getModelVarianceChecks = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/events/${eventId}/rounds/${roundId}/variance`,
      headers: this.headers()
    })
    return response.data
  }

  getModelSummarySetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/summary`,
      headers: this.headers()
    })
    return response.data
  }

  getModelFinancialsSetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/financials`,
      headers: this.headers()
    })
    return response.data
  }

  getModelOpexSetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/opex`,
      headers: this.headers()
    })
    return response.data
  }

  getModelTreasurySetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/treasury`,
      headers: this.headers()
    })
    return response.data
  }

  getModelPeerInsightsSetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/peer-insights`,
      headers: this.headers()
    })
    return response.data
  }

  getModelBusinessSetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/business`,
      headers: this.headers()
    })
    return response.data
  }

  getModelFinancialSummarySetup = async (eventId: string, roundId: number) => {
    const response = await get<any>({
      url: `${MODEL_HOST}/api/game-results/events/${eventId}/rounds/${roundId}/financials/summary`,
      headers: this.headers()
    })
    return response.data
  }

  getDefaultLayouts = async (reportingTemplateId: string, simulationId: string) => {
    const response = await get<API.ReportingTemplateLayoutResponse[]>({
      url: `${MODEL_HOST}/api/reporting-templates/${reportingTemplateId}/default-layouts?simulationId=${simulationId}`,
      headers: this.headers()
    })
    return response.data
  }

  getPreviewData = async (reportingTemplateId: string, simulationId: string, roundId: number, brandingId?: string) => {
    let url = `${MODEL_HOST}/api/reporting-preview/rounds/${roundId}?reportingTemplateId=${reportingTemplateId}&simulationId=${simulationId}`
    if (brandingId != null) {
      url += `&brandingId=${brandingId}`
    }
    const response = await get<ModelAPI.Reporting.ReportingResults>({
      url,
      headers: this.headers()
    })
    return response.data
  }
}



export default new ModelAPI()
